// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField } from '@mui/material';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import theme from 'Lib/theme';
import isEmpty from 'Lib/utilities/isEmpty';

import { EditorContext, useWizzyEditor } from './data/context';
import { WizzyButtonIcon, WizzyToolbar, WizzyToolbarButton } from './styledComponents';

// Link Modal Component
const LinkModal = (
	/** @type {{ open: boolean, onClose: () => void, onSubmit: (data: { text: string, url: string, openInNewTab: boolean}) => void, initialText?: string, initialUrl?: string, initialOpenInNewTab?: boolean, editor?: any }} */ {
		open,
		onClose,
		onSubmit,
		initialText = '',
		initialUrl = '',
		initialOpenInNewTab = true,
		editor
	}
) => {
	const [text, setText] = useState('');
	const [url, setUrl] = useState('');
	const [openInNewTab, setOpenInNewTab] = useState(true);

	useEffect(() => {
		setText(initialText);
		setUrl(initialUrl);
		setOpenInNewTab(initialOpenInNewTab);
	}, [initialText, initialUrl]);

	const handleSubmit = () => {
		// If there's selected text and we have new text, replace the selected text
		if (editor && text.trim() && text.trim() !== initialText) {
			const { from, to } = editor.state.selection;
			if (from !== to) {
				// Replace selected text with new text
				editor.chain().focus().deleteRange({ from, to }).run();
			}
		}

		onSubmit({ text: text.trim(), url: url.trim(), openInNewTab });
		onClose();
	};

	const handleClose = () => {
		setText(initialText);
		setUrl(initialUrl);
		setOpenInNewTab(initialOpenInNewTab);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add Link</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					<TextField label="Link Text" value={text} onChange={e => setText(e.target.value)} fullWidth autoFocus />
					<TextField label="URL" value={url} onChange={e => setUrl(e.target.value)} fullWidth placeholder="https://example.com" />
					<FormControlLabel control={<Checkbox checked={openInNewTab} onChange={e => setOpenInNewTab(e.target.checked)} />} label="Open in new tab" />
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSubmit} variant="contained">
					{initialUrl ? 'Update Link' : 'Add Link'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * @param {{ children: any, placeholder?: string, defaultContent?: string, extensions?: any[] } & Partial<import("@tiptap/core").EditorOptions>} props
 */
export function Wrapper({ children, placeholder = '', defaultContent = '', extensions = [], ...editorOptions }) {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: false,
				codeBlock: false
			}),
			Placeholder.configure({
				placeholder,
				emptyEditorClass: 'is-editor-empty'
			}),

			TextAlign.configure({
				types: ['heading', 'paragraph']
			}),
			Link.configure({
				openOnClick: false,
				autolink: true,
				defaultProtocol: 'https',
				protocols: ['http', 'https'],
				isAllowedUri: (url, ctx) => {
					try {
						// construct URL
						const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`);

						// use default validation
						if (!ctx.defaultValidate(parsedUrl.href)) {
							return false;
						}

						// disallowed protocols
						const disallowedProtocols = ['ftp', 'file', 'mailto'];
						const protocol = parsedUrl.protocol.replace(':', '');

						if (disallowedProtocols.includes(protocol)) {
							return false;
						}

						// only allow protocols specified in ctx.protocols
						const allowedProtocols = ctx.protocols.map(p => (typeof p === 'string' ? p : p.scheme));

						if (!allowedProtocols.includes(protocol)) {
							return false;
						}

						// disallowed domains
						const disallowedDomains = ['example-phishing.com', 'malicious-site.net'];
						const domain = parsedUrl.hostname;

						if (disallowedDomains.includes(domain)) {
							return false;
						}

						// all checks have passed
						return true;
					} catch {
						return false;
					}
				},
				shouldAutoLink: url => {
					try {
						// construct URL
						const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`);

						// only auto-link if the domain is not in the disallowed list
						const disallowedDomains = ['example-no-autolink.com', 'another-no-autolink.com'];
						const domain = parsedUrl.hostname;

						return !disallowedDomains.includes(domain);
					} catch {
						return false;
					}
				}
			}),
			Table.configure({
				resizable: true
			}),
			TextStyle.configure({ mergeNestedSpanStyles: true }),
			Color,
			Text,
			FontFamily,
			TableRow,
			TableHeader,
			TableCell,
			Image,
			Subscript,
			Underline,
			...extensions
		],
		...editorOptions
	});

	if (!editor) {
		return null;
	}

	return <EditorContext.Provider value={{ editor, defaultContent }}>{children}</EditorContext.Provider>;
}

export function Output() {
	const { editor } = useWizzyEditor();

	return <EditorContent editor={editor} />;
}

/**
 *
 * @param {{type: 'bold' | 'italic' | 'bulletList' | 'link' | 'underline' | 'strike' | 'subscript' | 'image' | 'horizontalRule' | 'alignLeft' | 'alignCenter' | 'alignRight' | 'alignJustify'}} param0
 * @returns
 */
export function ToggleAction({ type = 'bold' }) {
	const { editor } = useWizzyEditor();
	const [selected, setSelected] = useState(false);
	const [linkModalOpen, setLinkModalOpen] = useState(false);
	const buttonRef = useRef(null);

	if (!editor) {
		throw new Error('Editor is not available, make sure this is in a Tiptap component');
	}

	const updateButton = (/** @type {{ editor: any }} */ { editor }) => {
		if (editor.isActive(type)) {
			!selected && setSelected(true);

			return;
		}

		setSelected(false);
	};

	const setLink = useCallback(() => {
		// If we're in a link, select the entire link
		if (editor.isActive('link')) {
			editor.chain().focus().extendMarkRange('link').run();
		}
		setLinkModalOpen(true);
	}, [editor]);

	const handleLinkSubmit = useCallback(
		(/** @type {{ text: string, url: string, openInNewTab: boolean }} */ { text, url, openInNewTab }) => {
			if (!url) {
				// If no URL provided, remove the link
				editor.chain().focus().extendMarkRange('link').unsetLink().run();

				return;
			}

			// Get current selection
			const { from, to } = editor.state.selection;
			const selectedText = editor.state.doc.textBetween(from, to);

			// Prepare link attributes based on openInNewTab setting
			const linkAttrs = {
				href: url,
				...(!openInNewTab && { target: '', rel: '' })
			};

			try {
				if (selectedText) {
					// If text is selected, replace it with new text and add link
					if (text && text.trim() !== selectedText) {
						// Replace selected text with new text, then add link
						editor
							.chain()
							.focus()
							.deleteRange({ from, to })
							.insertContent(text.trim())
							.setTextSelection({ from, to: from + text.trim().length })
							.setLink(linkAttrs)
							.run();
					} else {
						// Just add the link to existing selection
						editor.chain().focus().setLink(linkAttrs).run();
					}
				} else if (text) {
					// If no text is selected but text is provided, insert text with link
					const linkHtml = !openInNewTab ? `<a href="${url}" target="" rel="">${text}</a>` : `<a href="${url}">${text}</a>`;
					editor.chain().focus().insertContent(linkHtml).run();
				} else {
					// If no text provided, use URL as text
					const linkHtml = !openInNewTab ? `<a href="${url}" target="" rel="">${url}</a>` : `<a href="${url}">${url}</a>`;
					editor.chain().focus().insertContent(linkHtml).run();
				}
			} catch (/** @type {any} */ e) {
				alert(e.message);
			}
		},
		[editor]
	);

	const addImage = () => {
		const url = window.prompt('URL');

		if (url) {
			editor.chain().focus().setImage({ src: url }).run();
		}
	};

	editor.on('selectionUpdate', updateButton);

	editor.on('update', updateButton);

	// Get current link attributes for modal
	const currentLinkAttrs = editor.getAttributes('link') ?? {};
	const currentUrl = currentLinkAttrs.href || '';
	const currentOpenInNewTab = isEmpty(currentLinkAttrs.target) || currentLinkAttrs.target === '_blank';

	const { from, to } = editor.state.selection;
	const currentText = editor.state.doc.textBetween(from, to);

	const iconActions = {
		alignLeft: {
			icon: 'format_align_left',
			onClick: () => editor.chain().focus().toggleTextAlign('left').run(),
			value: 'alignLeft',
			selected: selected || editor.isActive({ textAlign: 'left' }),
			'aria-label': 'alignLeft'
		},
		alignCenter: {
			icon: 'format_align_center',
			onClick: () => editor.chain().focus().toggleTextAlign('center').run(),
			value: 'alignCenter',
			selected: selected || editor.isActive({ textAlign: 'center' }),
			'aria-label': 'alignCenter'
		},
		alignRight: {
			icon: 'format_align_right',
			onClick: () => editor.chain().focus().toggleTextAlign('right').run(),
			value: 'alignRight',
			selected: selected || editor.isActive({ textAlign: 'right' }),
			'aria-label': 'alignRight'
		},
		alignJustify: {
			icon: 'format_align_justify',
			onClick: () => editor.chain().focus().toggleTextAlign('justify').run(),
			value: 'alignJustify',
			selected: selected || editor.isActive({ textAlign: 'justify' }),
			'aria-label': 'alignJustify'
		},
		bold: {
			icon: 'format_bold',
			onClick: () => editor.chain().focus().toggleBold().run(),
			disabled: !editor.can().chain().focus().toggleBold().run(),
			selected: selected || editor.isActive('bold'),
			value: 'bold',
			'aria-label': 'bold'
		},
		bulletList: {
			icon: 'format_list_bulleted',
			onClick: () => editor.chain().focus().toggleBulletList().run(),
			disabled: !editor.can().chain().focus().toggleBulletList().run(),
			value: 'bulletList',
			selected: selected || editor.isActive('bulletList'),
			'aria-label': 'bulletList'
		},
		orderedList: {
			icon: 'format_list_numbered',
			onClick: () => editor.chain().focus().toggleOrderedList().run(),
			disabled: !editor.can().chain().focus().toggleOrderedList().run(),
			value: 'orderedList',
			selected: selected || editor.isActive('orderedList'),
			'aria-label': 'orderedList'
		},
		horizontalRule: {
			icon: 'horizontal_rule',
			onClick: () => editor.chain().focus().setHorizontalRule().run(),
			disabled: !editor.can().chain().focus().setHorizontalRule().run(),
			value: 'horizontalRule',
			selected: selected || editor.isActive('horizontalRule'),
			'aria-label': 'horizontalRule'
		},
		image: {
			icon: 'photo_library',
			onClick: addImage,
			// disabled: !editor.can().chain().focus().setImage().run(),
			value: 'image',
			selected: selected || editor.isActive('image'),
			'aria-label': 'image'
		},
		italic: {
			icon: 'format_italic',
			onClick: () => editor.chain().focus().toggleItalic().run(),
			disabled: !editor.can().chain().focus().toggleItalic().run(),
			value: 'italic',
			selected: selected || editor.isActive('italic'),
			'aria-label': 'italic'
		},
		link: {
			icon: 'link',
			onClick: setLink,
			value: 'link',
			selected: selected || editor.isActive('link'),
			'aria-label': 'link'
		},
		underline: {
			icon: 'format_underlined',
			onClick: () => editor.chain().focus().toggleUnderline().run(),
			disabled: !editor.can().chain().focus().toggleUnderline().run(),
			value: 'underline',
			selected: selected || editor.isActive('underline'),
			'aria-label': 'underline'
		},
		strike: {
			icon: 'format_strikethrough',
			onClick: () => editor.chain().focus().toggleStrike().run(),
			disabled: !editor.can().chain().focus().toggleStrike().run(),
			value: 'strike',
			selected: selected || editor.isActive('strike'),
			'aria-label': 'strike'
		},
		subscript: {
			icon: 'subscript',
			onClick: () => editor.chain().focus().toggleSubscript().run(),
			disabled: !editor.can().chain().focus().toggleSubscript().run(),
			value: 'subscript',
			selected: selected || editor.isActive('subscript'),
			'aria-label': 'subscript'
		}
	};

	const props = iconActions[type];

	return (
		<>
			<WizzyToolbarButton ref={buttonRef} size="small" style={{ color: theme.palette.common.black }} {...props}>
				<WizzyButtonIcon>{props.icon}</WizzyButtonIcon>
			</WizzyToolbarButton>

			{type === 'link' && (
				<LinkModal
					open={linkModalOpen}
					onClose={() => setLinkModalOpen(false)}
					onSubmit={handleLinkSubmit}
					initialText={currentText}
					initialUrl={currentUrl}
					initialOpenInNewTab={currentOpenInNewTab}
					editor={editor}
				/>
			)}
		</>
	);
}

export default {
	Wrapper,
	Output,
	ToggleAction,
	Context: useWizzyEditor,
	ToggleActionIcon: WizzyButtonIcon,
	Toolbar: WizzyToolbar,
	CustomActionButton: WizzyToolbarButton
};
